import { useEffect, useState } from "react";
import { db } from "../../../firebase/firestore";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where
} from "firebase/firestore";
import { logError, logSuccess, getErrorMessage } from "../../../utils/errorHandler";

function getDisplayName(user) {
  const explicit = String(user?.name || "").trim();
  if (explicit) return explicit;

  const fromEmail = String(user?.email || "").trim();
  if (fromEmail.includes("@")) {
    return fromEmail.split("@")[0];
  }

  const uid = String(user?.id || "").trim();
  return uid ? `User ${uid.slice(0, 6)}` : "User";
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionStatus, setActionStatus] = useState("");

  // 🔥 FETCH USERS
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError("");
      logSuccess("Users", "Fetching users...");
      
      const snap = await getDocs(collection(db, "users"));
      const userData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      setUsers(userData);
      logSuccess("Users", `Loaded ${userData.length} users`);
    } catch (err) {
      logError("Fetch Users", err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // 🔥 FETCH USER ORDERS
  const fetchOrders = async (userId) => {
    try {
      setError("");
      logSuccess("User Orders", `Fetching orders for ${userId}...`);
      
      const q = query(
        collection(db, "orders"),
        where("userId", "==", userId)
      );

      const snap = await getDocs(q);
      const orderData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      setOrders(orderData);
      logSuccess("User Orders", `Loaded ${orderData.length} orders`);
    } catch (err) {
      logError("Fetch User Orders", err);
      setError(getErrorMessage(err));
    }
  };

  // 🔥 OPEN USER DETAILS
  const openUser = async (user) => {
    try {
      setSelectedUser(user);
      await fetchOrders(user.id);
    } catch (err) {
      logError("Open User", err);
      setError(getErrorMessage(err));
    }
  };

  // 🔥 BLOCK / UNBLOCK
  const toggleBlock = async (user) => {
    try {
      setActionStatus("");
      logSuccess("Toggle Block", `Updating user ${user.id}...`);
      
      await updateDoc(doc(db, "users", user.id), {
        blocked: !user.blocked
      });

      setActionStatus(user.blocked ? "✓ User unblocked" : "✓ User blocked");
      await fetchUsers();
      logSuccess("Toggle Block", "User updated successfully");
    } catch (err) {
      logError("Toggle Block", err);
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="users-page">

      <h1>👤 User Management</h1>

      {/* 🔥 ERROR DISPLAY */}
      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      {/* 🔥 ACTION STATUS */}
      {actionStatus && (
        <div className="success-banner">
          {actionStatus}
        </div>
      )}

      {/* 🔥 LOADING STATE */}
      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          ⏳ Loading users...
        </div>
      ) : (
        <>
          {/* 🔥 USERS TABLE */}
          <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Orders</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {users.map(user => (
              <tr key={user.id}>

                <td>{getDisplayName(user)}</td>

                <td>{user.phone}</td>

                <td>
                  {user.blocked ? (
                    <span className="blocked">Blocked</span>
                  ) : (
                    <span className="active">Active</span>
                  )}
                </td>

                <td>{user.orderCount || 0}</td>

                <td>
                  <button onClick={() => openUser(user)}>
                    View
                  </button>

                  <button onClick={() => toggleBlock(user)}>
                    {user.blocked ? "Unblock" : "Block"}
                  </button>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 🔥 USER DETAILS MODAL */}
      {selectedUser && (
        <div className="modal">

          <div className="modal-box">

            <h2>{getDisplayName(selectedUser)}</h2>

            <p><b>Phone:</b> {selectedUser.phone}</p>
            <p><b>Email:</b> {selectedUser.email}</p>
            <p><b>Address:</b> {selectedUser.address}</p>

            <h3>📦 Orders</h3>

            {orders.length === 0 ? (
              <p>No orders yet</p>
            ) : (
              orders.map(o => (
                <div key={o.id} className="order-card">
                  <p>₹{o.total}</p>
                  <p>{new Date(o.createdAt?.seconds * 1000).toLocaleDateString()}</p>
                </div>
              ))
            )}

            <button onClick={() => setSelectedUser(null)}>
              Close
            </button>

          </div>

        </div>
      )}
        </>
      )}

    </div>
  );
}